// pages/WebsiteLinks/ListTrainedData.js

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import DataTable from 'react-data-table-component';
import backendURL from "../../config"; // Your backend URL
import EditTrainedData from "./EditTrainedData"; // Path to your EditTrainedData component
import EditSummary from "./EditSummary"; // <<< NEW: Import EditSummary component
import ScrapeStatusModal from "./ScrapeStatusModal"; // Import the status modal
import { marked } from 'marked'; 

// MUI Imports for Modals, Menus, and buttons
import { 
    Modal as MuiModal, 
    Box, 
    Tooltip, 
    Button as MuiButton, 
    TextField, 
    Checkbox, 
    FormControlLabel, 
    Menu, 
    MenuItem, 
    IconButton,
    ListItemIcon,
    ListItemText
} from '@mui/material';

// Tiptap imports for PendingUpdateModal viewers
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';

// Helper delay function
const delay = (ms) => new Promise(res => setTimeout(res, ms));

// --- Start: Full Modal Components ---
function SyncSettingsModal({ isOpen, onClose, onSave, document, isLoading}) {
    const [isAutoSync, setIsAutoSync] = useState(document?.is_auto_sync ?? false);
    const [syncInterval, setSyncInterval] = useState(document?.sync_interval_days  > 0 ?  document?.sync_interval_days : document?.default_interval_days );

    useEffect(() => {
        setIsAutoSync(document?.is_auto_sync ?? false);
        let daysOfSyncInternal = document?.sync_interval_days  > 0 ?  document?.sync_interval_days : document?.default_interval_days
        setSyncInterval(daysOfSyncInternal?.toString() ?? ''); // Ensure string for TextField
    }, [document]);

    const handleSave = () => {
        const intervalVal = parseInt(syncInterval, 10);
        onSave({
            is_auto_sync: isAutoSync,
            sync_interval_days: (syncInterval === '' || isNaN(intervalVal) || intervalVal <= 0) ? null : intervalVal
        });
    };

    const modalStyle = {
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 500, bgcolor: 'background.paper', boxShadow: 24, p: 3, borderRadius: '8px', outline: 'none'
    };

    return (
        <MuiModal open={isOpen} onClose={onClose} aria-labelledby="sync-settings-title">
            <Box sx={modalStyle}>
                <h5 id="sync-settings-title" style={{marginTop: 0, marginBottom: '20px'}}>Sync Settings for:</h5>
                <p style={{wordBreak: 'break-all', marginBottom: '20px', fontSize: '0.9em', background: '#f9f9f9', padding: '8px', borderRadius: '4px'}}>{document?.website_link}</p>
                
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isAutoSync}
                            onChange={(e) => setIsAutoSync(e.target.checked)}
                        />
                    }
                    label="Enable Automatic Sync"
                    style={{ marginBottom: '15px' }}
                />
                <TextField
                    id="syncInterval"
                    label="Sync Interval (days)"
                    type="number"
                    placeholder="Default (e.g., 7)"
                    value={syncInterval}
                    onChange={(e) => setSyncInterval(e.target.value)}
                    disabled={!isAutoSync}
                    InputProps={{ inputProps: { min: 1 } }}
                    fullWidth
                    helperText={`Leave blank or 0 to use global default ${document?.default_interval_days} days. Active if auto-sync is ON.`}
                    variant="outlined"
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '25px' }}>
                    <MuiButton variant="outlined" onClick={onClose}> Cancel</MuiButton>
                    <MuiButton variant="contained" onClick={handleSave} disabled={isLoading} startIcon={isLoading && <i className="fa fa-spinner fa-spin"/>}> Save Settings</MuiButton>
                </Box>
            </Box>
        </MuiModal>
    );
}

function PendingUpdateModal({ isOpen, onClose, onAccept, onReject, originalContent, pendingContent, documentUrl , isLoading }) {
    const modalStyle = {
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: '90%', md: '85vw' }, maxWidth: '1400px', maxHeight: '90vh',
        bgcolor: 'background.paper', boxShadow: 24, p: 3, borderRadius: '8px', outline: 'none',
        display: 'flex', flexDirection: 'column'
    };

    const commonExtensions = [
        StarterKit.configure({heading: {levels: [1,2,3,4,5,6]}}), 
        Link.configure({openOnClick: false, autolink: true, linkOnPaste: true, HTMLAttributes: {target: '_blank'}}), 
        Underline, 
        Table.configure({resizable: true}), 
        TableCell, TableHeader, TableRow
    ];

    const originalEditor = useEditor({
        extensions: commonExtensions,
        content: originalContent || "<p>No original content.</p>",
        editable: false,
    });

    const pendingEditor = useEditor({
        extensions: commonExtensions,
        content: pendingContent || "<p>No pending content.</p>",
        editable: false,
    });

    useEffect(() => {
        if (originalEditor && originalContent && originalEditor.getHTML() !== originalContent) {
            originalEditor.commands.setContent(originalContent);
        }
    }, [originalContent, originalEditor]);

    useEffect(() => {
        if (pendingEditor && pendingContent && pendingEditor.getHTML() !== pendingContent) {
            pendingEditor.commands.setContent(pendingContent);
        }
    }, [pendingContent, pendingEditor]);

    return (
        <MuiModal open={isOpen} onClose={onClose} aria-labelledby="pending-update-title">
            <Box sx={modalStyle}>
                <h5 id="pending-update-title" style={{marginTop:0, marginBottom: '5px'}}>Review Pending Update</h5>
                <p style={{wordBreak: 'break-all', marginBottom: '15px', fontSize: '0.9em', background: '#f9f9f9', padding: '8px', borderRadius: '4px'}}>{documentUrl}</p>
                
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: '15px', flexGrow: 1, overflow: 'hidden' }}>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #ddd', borderRadius: '4px' }}>
                        <h6 style={{ padding: '10px 12px', backgroundColor: '#f0f0f0', margin: 0, borderBottom: '1px solid #ddd' }}>Current Version</h6>
                        <Box sx={{ flexGrow: 1, overflowY: 'auto', padding: '5px' }}>
                            {originalEditor ? <EditorContent editor={originalEditor} /> : <p>Loading...</p>}
                        </Box>
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #a0d3a0', borderRadius: '4px' }}>
                        <h6 style={{ padding: '10px 12px', backgroundColor: '#e6ffed', margin: 0, borderBottom: '1px solid #a0d3a0' }}>New Version</h6>
                        <Box sx={{ flexGrow: 1, overflowY: 'auto', padding: '5px' }}>
                            {pendingEditor ? <EditorContent editor={pendingEditor} /> : <p>Loading...</p>}
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px', flexShrink: 0 }}>
                    <MuiButton variant="outlined" color="error" onClick={() => onReject(originalEditor.getHTML())} disabled={isLoading} startIcon={isLoading === 'reject' && <i className="fa fa-spinner fa-spin"/>}>Reject</MuiButton>
                    <MuiButton variant="contained" color="primary" onClick={() => onAccept(pendingEditor.getHTML())} disabled={isLoading} startIcon={isLoading === 'accept' && <i className="fa fa-spinner fa-spin"/>}>Approve & Update</MuiButton>
                </Box>
            </Box>
        </MuiModal>
    );
}
// --- End: Full Modal Components ---

// --- ListTrainedData Component ---
export default function ListTrainedData({ refresh, setRefresh }) {
  const location = useLocation();
  
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSyncIntervalLoader, setIsSyncIntervalLoader] = useState(false);
  const [isProcessingPendingActionLoader, setIsProcessingPendingActionLoader] = useState(null);
  
  const [error, setError] = useState(null); 
  const [searchText, setSearchText] = useState("");
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditSummaryModalOpen, setIsEditSummaryModalOpen] = useState(false); // <<< NEW
  const [currentDocumentToEdit, setCurrentDocumentToEdit] = useState(null);
  
  const [refreshingRows, setRefreshingRows] = useState({});
  const [summaryRefreshingRows, setSummaryRefreshingRows] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const [selectionLimitReached, setSelectionLimitReached] = useState(false);

  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkDeleteResult, setBulkDeleteResult] = useState(null);

  const [isSyncSettingsModalOpen, setIsSyncSettingsModalOpen] = useState(false);
  const [isPendingUpdateModalOpen, setIsPendingUpdateModalOpen] = useState(false);
  const [currentDocumentForModals, setCurrentDocumentForModals] = useState(null); 
  const [currentPendingContent, setCurrentPendingContent] = useState("");

  const [isBulkRefreshing, setIsBulkRefreshing] = useState(false);
  const [bulkRefreshResults, setBulkRefreshResults] = useState([]);
  const [isBulkRefreshModalOpen, setIsBulkRefreshModalOpen] = useState(false);
  
  const [isBulkSummaryRefreshing, setIsBulkSummaryRefreshing] = useState(false);
  const [bulkSummaryRefreshResults, setBulkSummaryRefreshResults] = useState([]);
  const [isBulkSummaryRefreshModalOpen, setIsBulkSummaryRefreshModalOpen] = useState(false);

  const [isBulkArchiving, setIsBulkArchiving] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [currentRowForMenu, setCurrentRowForMenu] = useState(null);

  const MAX_SELECT_LIMIT = 99;

  const muiModalStyle = { 
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    width: { xs: '95%', sm: '90%', md: '70vw' }, maxWidth: '1000px', 
    maxHeight: '90vh', bgcolor: 'background.paper',
    border: 'none', borderRadius: '8px', boxShadow: 24, p: {xs: 2, sm: 3}, 
    display: 'flex', flexDirection: 'column', outline: 'none',
  };

  const fetchDocuments = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${backendURL}/get-trained-website-links`, { headers });
      const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data || [];
      setDocuments(responseData);
    } catch (err) {
       if (err.response && err.response.status === 401) {
         setError("Authentication error. Please log in again.");
       } else {
         setError(`Error fetching data: ${err.response?.data?.message || 'Unknown error'}`);
       }
       setDocuments([]);
    } finally {
        setLoading(false);
    }
  }, []);

  const applySearchFilter = useCallback((searchValue, sourceDocuments) => {
    const lowerCaseSearchValue = searchValue.toLowerCase();
    if (!lowerCaseSearchValue) {
      setFilteredDocuments(sourceDocuments);
      return;
    }
    const filtered = sourceDocuments.filter(doc => 
        doc.website_link?.toLowerCase().includes(lowerCaseSearchValue) ||
        doc.title_tag?.toLowerCase().includes(lowerCaseSearchValue) ||
        doc.page_content?.toLowerCase().includes(lowerCaseSearchValue)
    );
    setFilteredDocuments(filtered);
  }, []);

  useEffect(() => {
    document.title = 'Trained Data | KlonIT';
    fetchDocuments();
  }, [refresh, fetchDocuments]); 

  useEffect(() => {
    applySearchFilter(searchText, documents);
  }, [searchText, documents, applySearchFilter]);

   useEffect(() => {
    setSelectionLimitReached(selectedRows.length >= MAX_SELECT_LIMIT);
    if (filteredDocuments.length > 0) {
        const allVisibleAreSelected = filteredDocuments.length <= MAX_SELECT_LIMIT && 
                                   filteredDocuments.every(doc => selectedRows.some(sel => sel.id === doc.id));
        setIsSelectAllChecked(allVisibleAreSelected && selectedRows.length > 0); 
    } else {
      setIsSelectAllChecked(false);
    }
  }, [selectedRows, filteredDocuments]);

  const handleOpenMenu = (event, row) => {
    setMenuAnchorEl(event.currentTarget);
    setCurrentRowForMenu(row);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setCurrentRowForMenu(null);
  };
  
  const handleView = (url) => window.open(url, '_blank', 'noopener,noreferrer');

  const handleSelectAll = () => {
    setError(null);
    if (isSelectAllChecked) { 
      const currentlyVisibleIds = new Set(filteredDocuments.map(doc => doc.id));
      setSelectedRows(prev => prev.filter(row => !currentlyVisibleIds.has(row.id)));
    } else { 
      const selectedIds = new Set(selectedRows.map(row => row.id));
      const availableRowsToSelect = filteredDocuments.filter(doc => !selectedIds.has(doc.id));
      const remainingCapacity = MAX_SELECT_LIMIT - selectedRows.length;
      
      if (remainingCapacity <= 0 && availableRowsToSelect.length > 0) {
        setError(`Maximum selection limit of ${MAX_SELECT_LIMIT} rows reached.`);
        return;
      }
      const rowsToAdd = availableRowsToSelect.slice(0, remainingCapacity);
      setSelectedRows(prev => [...prev, ...rowsToAdd]);

      if (rowsToAdd.length < availableRowsToSelect.length) {
        setError(`Selection limit of ${MAX_SELECT_LIMIT} reached. ${rowsToAdd.length} additional rows selected.`);
      }
    }
  };

  const handleRowSelect = (row) => {
     setError(null);
     const isSelected = selectedRows.some((r) => r.id === row.id);
     if (isSelected) {
       setSelectedRows(prev => prev.filter((r) => r.id !== row.id));
     } else {
       if (selectedRows.length >= MAX_SELECT_LIMIT) {
           setError(`Maximum selection limit of ${MAX_SELECT_LIMIT} rows reached.`);
           return;
       }
       setSelectedRows(prev => [...prev, row]);
     }
  };

  const columns = [
    {
      name: (
        <Tooltip title={selectionLimitReached ? `Limit of ${MAX_SELECT_LIMIT} rows selected` : (isSelectAllChecked ? "Unselect all visible" : "Select all visible (up to limit)")} arrow>
          <span>
            <input type="checkbox" checked={isSelectAllChecked} onChange={handleSelectAll} aria-label="Select all visible rows" disabled={filteredDocuments.length === 0 || (selectionLimitReached && !isSelectAllChecked)} />
          </span>
        </Tooltip>
      ),
      cell: (row) => ( <input type="checkbox" checked={selectedRows.some((r) => r.id === row.id)} onChange={() => handleRowSelect(row)} disabled={selectionLimitReached && !selectedRows.some((r) => r.id === row.id)} /> ),
      width: '60px', ignoreRowClick: true, allowOverflow: true, button: true,
    },
    { name: 'URL', selector: row => row.website_link, sortable: true, maxWidth: '300px', cell: row => (<Tooltip title={row.website_link} arrow><span className="ellipsis-text">{row.website_link}</span></Tooltip>) },
    { name: 'Page Name', selector: row => row.title_tag, sortable: true, maxWidth: '300px', cell: row => (<Tooltip title={row.title_tag} arrow><span className="ellipsis-text">{row.title_tag || '(No Title)'}</span></Tooltip>) },
    { name: 'Auto Sync', selector: row => row.is_auto_sync, sortable: true, maxWidth: '120px', center: true, cell: row => (
        <Tooltip title={row.is_auto_sync ? "Auto-sync is ON. Click to change." : "Auto-sync is OFF. Click to change."} arrow>
            <i className={`fa ${row.is_auto_sync ? 'fa-toggle-on text-success' : 'fa-toggle-off text-secondary'}`} style={{ fontSize: '1.6em', cursor: 'pointer' }} onClick={() => handleOpenSyncSettings(row)} />
        </Tooltip>
    )},
    { name: 'Sync Schedule', selector: row => row.sync_interval_days, sortable: true, maxWidth: '130px', center: true, cell: row => (
        <Tooltip title="Click to change sync interval" arrow>
            <span onClick={() => handleOpenSyncSettings(row)} style={{ cursor: 'pointer', textDecoration: 'underline', color: '#0d6efd' }}>
                {
                  row.sync_interval_days
                    ? (row.sync_interval_days < 2
                        ? `Every ${row.sync_interval_days} day`
                        : `Every ${row.sync_interval_days} days`)
                    : 'Default'
                }
            </span>
        </Tooltip>
    )},
    { name: 'Last Synced', selector: row => row.last_sync_check_date, sortable: true, format: row => row.last_sync_check_date ? new Date(row.last_sync_check_date).toLocaleString() : 'Never', maxWidth: '190px' },
    { name: 'Status', maxWidth: '150px', center: true, cell: row => (
        row.has_pending_update ? (
            <Tooltip title="Content has an external update. Review changes." arrow>
                <MuiButton size="small" variant="outlined" color="warning" onClick={() => handleOpenPendingUpdateModal(row)} startIcon={<i className="fa fa-exclamation-triangle" />} style={{padding: '2px 8px', fontSize: '0.8rem'}}>
                    Pending
                </MuiButton>
            </Tooltip>
        ) : (
             <Tooltip title={row.is_trained ? "Data is trained" : "Data not trained / training pending"} arrow>
                 <i className={`fa ${row.is_trained ? 'fa-check-circle text-success' : 'fa-hourglass-o text-secondary'}`} style={{ fontSize: '1.5em' }} />
             </Tooltip>
        )
    )},
    { 
      name: 'Actions', 
      width: '60px', 
      cell: (row) => (
        <IconButton
          aria-label="actions"
          onClick={(e) => handleOpenMenu(e, row)}
        >
          <i className="fa fa-ellipsis-v"></i>
        </IconButton>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const ExpandedRow = ({ data }) => {
    const cleanedSummaryMd = (data.summary || '').replace(/^text\s*/, '');
    const summaryHtml = cleanedSummaryMd ? marked.parse(cleanedSummaryMd) : 'N/A';
    const contentHtml = data.page_content ? marked.parse(data.page_content) : 'N/A';

    return (
      <div style={{ padding: '15px', borderLeft: '3px solid #0d6efd', backgroundColor: '#f8f9fa', fontSize: '0.9em' }}>
        <p><strong>URL ID:</strong> {data.id}</p>
        <p><strong>Website Link:</strong> <a href={data.website_link} target="_blank" rel="noopener noreferrer">{data.website_link}</a></p>
        <p><strong>Title Tag:</strong> {data.title_tag || 'N/A'}</p>

        <div style={{ margin: '10px 0' }}>
          <strong>Summary (HTML Source):</strong>
          <textarea
            className="code-textarea"
            readOnly
            value={summaryHtml}
            rows={15}
          />
        </div>

        <div style={{ margin: '10px 0' }}>
          <strong>Content (HTML Source):</strong>
          <textarea
            className="code-textarea"
            readOnly
            value={contentHtml}
            rows={20}
          />
        </div>

        <p><strong>Auto Sync:</strong> {data.is_auto_sync ? 'Enabled' : 'Disabled'}</p>
        <p><strong>Sync Schedule:</strong> {data.sync_interval_days ? `${data.sync_interval_days} days` : 'Default'}</p>
        <p><strong>Last Synced:</strong> {data.last_sync_check_date ? new Date(data.last_sync_check_date).toLocaleString() : 'Never'}</p>
        <p><strong>Pending Update:</strong> {data.has_pending_update ? 'Yes' : 'No'}</p>
        <p><strong>Trained:</strong> {data.is_trained ? 'Yes' : 'No'}</p>
        <p><strong>H1:</strong> {data.h1_tags || 'N/A'}</p>
        <p><strong>H2:</strong> {data.h2_tags || 'N/A'}</p>
        <p><strong>H3:</strong> {data.h3_tags || 'N/A'}</p>
        <p><strong>Created:</strong> {data.created_date ? new Date(data.created_date).toLocaleString() : 'N/A'}</p>
        <p><strong>Last Updated (DB):</strong> {data.updated_date ? new Date(data.updated_date).toLocaleString() : 'N/A'}</p>
      </div>
    );
  };
  
  const handleDelete = async (documentId) => {
     if (!window.confirm(`Delete item ID ${documentId}? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('access_token');
      setError(null); 
      await axios.delete(`${backendURL}/delete-website-scrape-data/${documentId}`, { headers: { Authorization: `Bearer ${token}` } });
      setDocuments(prevDocs => prevDocs.filter(doc => doc.id !== documentId));
      setSelectedRows(prevSelected => prevSelected.filter(row => row.id !== documentId));
      setError("Item deleted successfully."); 
    } catch (err) {
      setError(`Error deleting item: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleArchive = async (documentId) => {
    if (!window.confirm(`Archive item ID ${documentId}? It will be moved to the Archive tab and removed from the active training set.`)) return;
    setError(null);
    try {
        const token = localStorage.getItem('access_token');
        const headers = { Authorization: `Bearer ${token}` };
        await axios.post(`${backendURL}/archive-website-data`, { link_ids: [documentId] }, { headers });
        setError("Item archived successfully.");
        fetchDocuments();
        if (setRefresh) setRefresh(prev => !prev);
    } catch (err) {
        setError(`Error archiving item: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleBulkArchive = async () => {
    if (selectedRows.length === 0 || isBulkArchiving) return;
    if (!window.confirm(`Archive ${selectedRows.length} selected item(s)? They will be moved to the Archive tab.`)) return;

    setIsBulkArchiving(true);
    setError(null);
    const idsToArchive = selectedRows.map(row => row.id);

    try {
        const token = localStorage.getItem('access_token');
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.post(`${backendURL}/archive-website-data`, { link_ids: idsToArchive }, { headers });
        setError(response.data.message || `${idsToArchive.length} items archived successfully.`);
        setSelectedRows([]);
        fetchDocuments();
        if (setRefresh) setRefresh(prev => !prev);
    } catch (err) {
        setError(`Error during bulk archive: ${err.response?.data?.message || err.message}`);
    } finally {
        setIsBulkArchiving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0 || isBulkDeleting) return;
    if (!window.confirm(`Delete ${selectedRows.length} selected item(s)? This cannot be undone.`)) return;

    setIsBulkDeleting(true);
    setBulkDeleteResult(null);
    const initialSelectedCount = selectedRows.length;
    const token = localStorage.getItem('access_token');
    const idsToDelete = selectedRows.map(row => row.id);
    setError(null);

    let deletedCount = 0;
    let failedIds = [];
    for (const id of idsToDelete) {
       try {
           await axios.delete(`${backendURL}/delete-website-scrape-data/${id}`, { headers: { Authorization: `Bearer ${token}` } });
           deletedCount++;
       } catch (innerError) {
           failedIds.push(id);
       }
    }

    let resultMessage = `${deletedCount} item(s) deleted successfully.`;
    if (failedIds.length > 0) {
        resultMessage += ` ${failedIds.length} failed.`;
    }
    setError(resultMessage); 
    
    setBulkDeleteResult({ type: deletedCount > 0 ? 'success' : 'error', count: deletedCount, initialSelectedCount });

    if(deletedCount > 0) {
      setDocuments(prevDocs => prevDocs.filter(doc => !idsToDelete.includes(doc.id) || failedIds.includes(doc.id)));
      setSelectedRows([]);
    }
    
    setTimeout(() => setBulkDeleteResult(null), 4000);
    setIsBulkDeleting(false);
  };

  const handleSearch = (event) => setSearchText(event.target.value);

  const handleOpenEditModal = (document) => {
    setCurrentDocumentToEdit(document);
    setIsEditModalOpen(true);
    handleCloseMenu();
  };
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setCurrentDocumentToEdit(null);
  };
  const handleUpdateFromEditModal = () => {
    fetchDocuments(); 
    handleCloseEditModal();
  };
  
  // <<< START: New handlers for Edit Summary Modal
  const handleOpenEditSummaryModal = (document) => {
    setCurrentDocumentToEdit(document);
    setIsEditSummaryModalOpen(true);
    handleCloseMenu();
  };

  const handleCloseEditSummaryModal = () => {
    setIsEditSummaryModalOpen(false);
    setCurrentDocumentToEdit(null);
  };

  const handleUpdateFromEditSummaryModal = () => {
    fetchDocuments();
    handleCloseEditSummaryModal();
  };
  // <<< END: New handlers for Edit Summary Modal

  const handleOpenSyncSettings = (document) => {
    setCurrentDocumentForModals(document);
    setIsSyncSettingsModalOpen(true);
  };
  const handleCloseSyncSettings = () => {
    setIsSyncSettingsModalOpen(false);
    setCurrentDocumentForModals(null);
  };
  const handleSaveSyncSettings = async (settings) => {
    if (!currentDocumentForModals) return;
    setError(null);
    setIsSyncIntervalLoader(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
          updates: [{
              id: currentDocumentForModals.id,
              is_auto_sync: settings.is_auto_sync,
              sync_interval_days: settings.sync_interval_days 
          }]
      };
      await axios.post(`${backendURL}/update_website_link_sync_settings`, payload, { headers });
      setError("Sync settings updated successfully.");
      fetchDocuments(); 
      handleCloseSyncSettings();
    } catch (err) {
      setError(`Failed to update sync settings: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsSyncIntervalLoader(false);
    }
  };

  const handleOpenPendingUpdateModal = async (document) => {
    setCurrentDocumentForModals(document);
    setError(null);
    setCurrentPendingContent(document.pending_update_content); 
    setIsPendingUpdateModalOpen(true); 
  };
  const handleClosePendingUpdateModal = () => {
    setIsPendingUpdateModalOpen(false);
    setCurrentDocumentForModals(null);
    setCurrentPendingContent("");
  };
  const handleResolvePendingUpdate = async (action , pageData) => {
    if (!currentDocumentForModals) return;
    setError(null);
    setIsProcessingPendingActionLoader(action)
    try {
        const token = localStorage.getItem('access_token');
        const headers = { Authorization: `Bearer ${token}` };
        const payload = { id: currentDocumentForModals.id, page_content: pageData , all_data: currentDocumentForModals };
        await axios.post(`${backendURL}/update-trained-data`, payload, { headers });
        setError(`Pending update ${action === 'accept' ? 'accepted and processed' : 'rejected'} successfully.`);
        fetchDocuments(); 
        handleClosePendingUpdateModal();
    } catch (err) {
        setError(`Failed to ${action} pending update: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsProcessingPendingActionLoader(null)
    }
  };

  const handleRefresh = async (document) => {
    setRefreshingRows(prev => ({ ...prev, [document.id]: true }));
    setError(null); 
    try {
      const token = localStorage.getItem('access_token');
      const payload = { data: [ [document.id], [document.website_link] ] }; 
      const response = await axios.post(`${backendURL}/refresh-website-data`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setError(response.data.message || "Data refresh and retrain process has been completed.");
    } catch (err) {
      setError(`Error refreshing data: ${err.response?.data?.message || 'Unknown error'}`);
    } finally {
      setTimeout(() => setRefreshingRows(prev => { 
        const newState = { ...prev }; 
        delete newState[document.id]; 
        return newState; 
      }), 1500);
    }
  };

  const handleSummaryRefresh = async (document) => {
    setSummaryRefreshingRows(prev => ({ ...prev, [document.id]: true }));
    setError(null);
    try {
        const token = localStorage.getItem('access_token');
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.post(`${backendURL}/refresh-summary/${document.id}`, {}, { headers });
        setError(response.data.message || 'Summary refresh completed successfully.');
        fetchDocuments();
    } catch (err) {
        setError(`Error: ${err.response?.data?.message || 'Failed to refresh summary.'}`);
    } finally {
        setTimeout(() => {
            setSummaryRefreshingRows(prev => {
                const newState = { ...prev };
                delete newState[document.id];
                return newState;
            });
        }, 1500);
    }
  };

  const handleBulkRefresh = async () => {
    if (selectedRows.length === 0 || selectedRows.length > MAX_SELECT_LIMIT) {
        setError(`Please select between 1 and ${MAX_SELECT_LIMIT} rows to refresh.`);
        return;
    }
    setIsBulkRefreshing(true);
    setError(null); 
    const initialResults = selectedRows.map(doc => ({ website_link: doc.website_link, status: 'pending', message: null }));
    setBulkRefreshResults([...initialResults]); 
    setIsBulkRefreshModalOpen(true);

    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
    let currentProcessingResults = [...initialResults]; 

    for (let i = 0; i < selectedRows.length; i++) {
        const doc = selectedRows[i];
        try {
            const payload = { data: [[doc.id], [doc.website_link]] };
            const response = await axios.post(`${backendURL}/refresh-website-data`, payload, { headers });
            currentProcessingResults[i] = { ...currentProcessingResults[i], status: 'success', message: response.data?.message || "Refresh completed." };
        } catch (err) {
            currentProcessingResults[i] = { ...currentProcessingResults[i], status: 'error', message: err.response?.data?.message || err.message };
        }
        setBulkRefreshResults([...currentProcessingResults]); 
        if (i < selectedRows.length - 1) await delay(1000); 
    }
    setIsBulkRefreshing(false);
  };

  const handleBulkSummaryRefresh = async () => {
    if (selectedRows.length === 0 || selectedRows.length > MAX_SELECT_LIMIT) {
        setError(`Please select between 1 and ${MAX_SELECT_LIMIT} rows to refresh summaries.`);
        return;
    }
    setIsBulkSummaryRefreshing(true);
    setError(null);
    const initialResults = selectedRows.map(doc => ({ website_link: doc.website_link, status: 'pending', message: null }));
    setBulkSummaryRefreshResults([...initialResults]);
    setIsBulkSummaryRefreshModalOpen(true);

    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
    let currentProcessingResults = [...initialResults];

    for (let i = 0; i < selectedRows.length; i++) {
        const doc = selectedRows[i];
        try {
            const response = await axios.post(`${backendURL}/refresh-summary/${doc.id}`, {}, { headers });
            currentProcessingResults[i] = { ...currentProcessingResults[i], status: 'success', message: response.data?.message || "Summary refresh completed." };
        } catch (err) {
            currentProcessingResults[i] = { ...currentProcessingResults[i], status: 'error', message: err.response?.data?.message || 'Summary refresh failed' };
        }
        setBulkSummaryRefreshResults([...currentProcessingResults]);
        if (i < selectedRows.length - 1) await delay(500);
    }
    setIsBulkSummaryRefreshing(false);
  };

  let showDeleteButton = selectedRows.length > 0 || isBulkDeleting || (bulkDeleteResult && bulkDeleteResult.type === 'success');
  let deleteButtonIconClass = "fa fa-trash-o";
  let deleteButtonText = `Delete Selected (${selectedRows.length})`;
  let deleteButtonDisabled = selectedRows.length === 0 || isBulkDeleting || isBulkRefreshing || isBulkSummaryRefreshing || isBulkArchiving;
  let deleteButtonClass = "btn btn-danger";

  if (isBulkDeleting) {
      deleteButtonIconClass = "fa fa-spinner fa-spin"; deleteButtonText = "Deleting..."; deleteButtonDisabled = true;
  } else if (bulkDeleteResult) {
      if (bulkDeleteResult.type === 'success' && bulkDeleteResult.count > 0) {
          deleteButtonIconClass = "fa fa-check"; deleteButtonText = `Deleted (${bulkDeleteResult.count})`; deleteButtonDisabled = true; deleteButtonClass = "btn btn-success";
      } else if (bulkDeleteResult.type === 'error' || (bulkDeleteResult.type === 'success' && bulkDeleteResult.count === 0)) {
          deleteButtonIconClass = "fa fa-exclamation-triangle"; deleteButtonText = `Delete Selected (${bulkDeleteResult.initialCount})`; deleteButtonDisabled = false;
      }
  }

  return (
    <div className="row">
      <div className="col-12">
        <div className="card flex-fill">
          <div className="card-header">
             <h5 className="card-title mb-0">Trained Website Data</h5>
          </div>
          <div className="card-body">
            <div className="row mb-3 align-items-center">
              <div className="col-lg-8 col-md-7 mb-2 mb-md-0 d-flex flex-wrap gap-2"> 
                  {showDeleteButton && (
                    <button className={deleteButtonClass} onClick={handleBulkDelete} disabled={deleteButtonDisabled}>
                      <i className={`${deleteButtonIconClass} me-1`}></i> {deleteButtonText}
                    </button>
                  )}
                  {selectedRows.length > 0 && (
                      <button
                          className={`btn btn-info ${isBulkRefreshing ? 'disabled' : ''}`}
                          onClick={handleBulkRefresh}
                          disabled={deleteButtonDisabled}
                      >
                          {isBulkRefreshing ? <><i className="fa fa-spinner fa-spin me-1"></i> Refreshing...</> : <><i className="fa fa-sync me-1"></i> Bulk Refresh ({selectedRows.length})</>}
                      </button>
                  )}
                  {selectedRows.length > 0 && (
                      <button
                          className={`btn btn-warning ${isBulkSummaryRefreshing ? 'disabled' : ''}`}
                          onClick={handleBulkSummaryRefresh}
                          disabled={deleteButtonDisabled}
                      >
                          {isBulkSummaryRefreshing ? <><i className="fa fa-spinner fa-spin me-1"></i> Refreshing...</> : <><i className="fa fa-bolt me-1"></i> Bulk Summary Refresh ({selectedRows.length})</>}
                      </button>
                  )}
                  {selectedRows.length > 0 && (
                      <button
                          className="btn btn-secondary"
                          onClick={handleBulkArchive}
                          disabled={deleteButtonDisabled}
                      >
                          {isBulkArchiving ? <><i className="fa fa-spinner fa-spin me-1"></i> Archiving...</> : <><i className="fa fa-archive me-1"></i> Bulk Archive ({selectedRows.length})</>}
                      </button>
                  )}
              </div>
               <div className="col-lg-4 col-md-5"> 
                 <div className="input-group">
                    <input type="text" className="form-control" placeholder="Search URL, Title, Content..." value={searchText} onChange={handleSearch} />
                 </div>
              </div>
            </div>

            {error && (
                <div className={`alert ${error.toLowerCase().includes("success") || error.toLowerCase().includes("deleted") ? "alert-success" : "alert-danger"} alert-dismissible fade show`} role="alert">
                  {error}
                  <button type="button" className="btn-close" onClick={() => setError(null)} aria-label="Close"></button>
                </div>
            )}

            <div className="table-responsive">
               {loading ? ( <div className="text-center p-5"> <i className="fa fa-spinner fa-spin fa-3x"></i> <p className="mt-2">Loading Trained Data...</p> </div>
               ) : documents.length === 0 && !searchText ? ( <p className="text-center p-3">No trained website data found. Add links and scrape them to see data here.</p>
               ) : filteredDocuments.length === 0 && searchText ? ( <p className="text-left p-3">No results found for "{searchText}".</p>
               ) : (
                 <DataTable
                   columns={columns}
                   data={filteredDocuments}
                   pagination
                   responsive
                   highlightOnHover
                   pointerOnHover
                   expandableRows
                   expandableRowsComponent={ExpandedRow}
                   expandOnRowClicked
                   paginationPerPage={10}
                   paginationRowsPerPageOptions={[10, 25, 50, 100]}
                 />
               )}
            </div>
          </div>
        </div>
      </div>
      
        <Menu
            id="actions-menu"
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleCloseMenu}
            PaperProps={{ style: { maxHeight: 48 * 7, width: '25ch' } }} // Increased height
        >
            <MenuItem onClick={() => { handleView(currentRowForMenu.website_link); handleCloseMenu(); }}>
                <ListItemIcon><i className="fa fa-eye" style={{fontSize: '1.2em'}}></i></ListItemIcon>
                <ListItemText>View Website</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { handleRefresh(currentRowForMenu); handleCloseMenu(); }} disabled={!!refreshingRows[currentRowForMenu?.id]}>
                <ListItemIcon>{refreshingRows[currentRowForMenu?.id] ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-refresh" style={{fontSize: '1.2em'}}></i>}</ListItemIcon>
                <ListItemText>Refresh & Retrain</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { handleSummaryRefresh(currentRowForMenu); handleCloseMenu(); }} disabled={!!summaryRefreshingRows[currentRowForMenu?.id]}>
                <ListItemIcon>{summaryRefreshingRows[currentRowForMenu?.id] ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-bolt" style={{color: '#fd7e14', fontSize: '1.2em'}}></i>}</ListItemIcon>
                <ListItemText>Summary Refresh</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleOpenEditModal(currentRowForMenu)}>
                <ListItemIcon><i className="fa fa-pencil" style={{fontSize: '1.2em'}}></i></ListItemIcon>
                <ListItemText>Edit Content</ListItemText>
            </MenuItem>
            {/* <<< NEW MENU ITEM >>> */}
            <MenuItem onClick={() => handleOpenEditSummaryModal(currentRowForMenu)}>
                <ListItemIcon><i className="fa fa-file-text-o" style={{fontSize: '1.2em'}}></i></ListItemIcon>
                <ListItemText>Edit Summary</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { handleArchive(currentRowForMenu.id); handleCloseMenu(); }}>
                <ListItemIcon><i className="fa fa-archive" style={{fontSize: '1.2em'}}></i></ListItemIcon>
                <ListItemText>Archive</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { handleDelete(currentRowForMenu.id); handleCloseMenu(); }} sx={{color: 'red'}}>
                <ListItemIcon><i className="fa fa-trash-o" style={{color: 'red', fontSize: '1.2em'}}></i></ListItemIcon>
                <ListItemText>Delete</ListItemText>
            </MenuItem>
        </Menu>

        <MuiModal open={isEditModalOpen} onClose={handleCloseEditModal}>
            <Box sx={muiModalStyle}>
                {currentDocumentToEdit && (
                <EditTrainedData document={currentDocumentToEdit} onClose={handleCloseEditModal} onUpdate={handleUpdateFromEditModal} />
                )}
            </Box>
        </MuiModal>
        
        {/* <<< NEW MODAL FOR SUMMARY >>> */}
        <MuiModal open={isEditSummaryModalOpen} onClose={handleCloseEditSummaryModal}>
            <Box sx={muiModalStyle}>
                {currentDocumentToEdit && (
                <EditSummary document={currentDocumentToEdit} onClose={handleCloseEditSummaryModal} onUpdate={handleUpdateFromEditSummaryModal} />
                )}
            </Box>
        </MuiModal>

        {currentDocumentForModals && ( <SyncSettingsModal isOpen={isSyncSettingsModalOpen} onClose={handleCloseSyncSettings} onSave={handleSaveSyncSettings} document={currentDocumentForModals} isLoading={isSyncIntervalLoader}/> )}
        {currentDocumentForModals && ( <PendingUpdateModal isOpen={isPendingUpdateModalOpen} onClose={handleClosePendingUpdateModal} onAccept={(pageData) => handleResolvePendingUpdate('accept', pageData)} onReject={(pageData) => handleResolvePendingUpdate('reject', pageData)} originalContent={currentDocumentForModals.page_content} pendingContent={currentPendingContent} documentUrl={currentDocumentForModals.website_link} isLoading={isProcessingPendingActionLoader}/> )}

        <ScrapeStatusModal isOpen={isBulkRefreshModalOpen} onRequestClose={() => setIsBulkRefreshModalOpen(false)} scrapeResults={bulkRefreshResults} modalTitle="Bulk Refresh Status" />
        <ScrapeStatusModal isOpen={isBulkSummaryRefreshModalOpen} onRequestClose={() => setIsBulkSummaryRefreshModalOpen(false)} scrapeResults={bulkSummaryRefreshResults} modalTitle="Bulk Summary Refresh Status" />
        
        <style jsx global>{`
            .ellipsis-text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; max-width: 100%; }
            .card-body .table-responsive .rdt_TableCol_Sortable { font-weight: 600 !important; }
            .code-textarea {
                width: 100%;
                border: 1px solid #ccc;
                padding: 10px;
                background-color: #fff;
                margin-top: 5px;
                font-family: 'Courier New', Courier, monospace;
                font-size: 0.9em;
                word-wrap: break-word;
                resize: vertical;
                min-height: 100px;
            }
        `}</style>
    </div>
  );
}